import React, { useState, useEffect } from "react";
import { Plus, Save, Power, PowerOff } from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";
import { facilitiesApi } from "../../utils/api";
import { SERVICE_TYPES } from "../../constants";
import FacilityDetail from "./FacilityDetail";

const SERVICE_DOT = {
  active: "bg-green-500",
  provisioning: "bg-amber-400",
  suspended: "bg-red-500",
  decommissioned: "bg-slate-300",
};

// Small badge per provisioned service with a status dot
const FacilityServiceBadges = ({ services }) => {
  if (!Array.isArray(services) || services.length === 0)
    return <span className="text-xs text-slate-400">No services</span>;
  return (
    <div className="flex flex-wrap gap-1 max-w-[220px]">
      {services.map((s) => (
        <span
          key={s.service_type}
          title={`${s.service_type}: ${s.status}`}
          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700"
        >
          <span
            className={`w-1.5 h-1.5 rounded-full mr-1.5 ${SERVICE_DOT[s.status] || "bg-slate-300"}`}
          />
          {SERVICE_TYPES.find((t) => t.value === s.service_type)?.label ||
            s.service_type}
        </span>
      ))}
    </div>
  );
};

const VERTICALS = [
  { value: "healthcare", label: "Healthcare" },
  { value: "legal", label: "Legal" },
  { value: "finance", label: "Finance" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail", label: "Retail" },
  { value: "construction", label: "Construction" },
  { value: "political", label: "Political" },
  { value: "informational", label: "Informational" },
];

const MODEL_TIERS = [
  { value: "basic", label: "Basic (CPU)" },
  { value: "medium", label: "Medium (T4 Small)" },
  { value: "elite", label: "Elite (T4 Medium)" },
  { value: "premium", label: "Premium (A10G)" },
];

const BASE_MODELS = [
  { value: "biomistral-7b", label: "BioMistral-7B (Medical)" },
  { value: "mistral-7b", label: "Mistral-7B-Instruct (General)" },
  { value: "gemma-7b-it", label: "Gemma-7B-IT (General)" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern" },
  { value: "America/Chicago", label: "Central" },
  { value: "America/Denver", label: "Mountain" },
  { value: "America/Phoenix", label: "Arizona" },
  { value: "America/Los_Angeles", label: "Pacific" },
  { value: "UTC", label: "UTC" },
];

const FacilityManagement = ({ onNavigate }) => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingFacility, setEditingFacility] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [expandedFacility, setExpandedFacility] = useState(null);

  const emptyForm = {
    facility_id: "",
    facility_name: "",
    facility_type: "business",
    contact_email: "",
    contact_phone: "",
    address: "",
    timezone: "America/New_York",
    status: "active",
    vertical_id: "retail",
    base_model_id: "mistral-7b",
    model_tier: "premium",
    training_enabled: true,
    webhook_url: "",
    webhook_enabled: false,
    display_name: "",
    state: "",
    agent_name: "",
    brand_color: "#cc1111",
    static_base_url: "",
    allowed_origins: "",
    system_prompt: "",
    enrichment_rules: "[]",
    sheets_log_tab: "Logs",
    source_registry_namespace: "",
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    setLoading(true);
    try {
      const data = await facilitiesApi.getAll();
      setFacilities(data);
    } catch (error) {
      console.error("Error fetching facilities:", error);
    }
    setLoading(false);
  };

  const getField = (obj, ...keys) => {
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingFacility) {
        await facilitiesApi.update(editingFacility.id, formData);
      } else {
        await facilitiesApi.create(formData);
      }
      fetchFacilities();
      resetForm();
    } catch (error) {
      console.error("Error saving facility:", error);
    }
    setLoading(false);
  };

  const handleEdit = (facility) => {
    setSelectedFacility(facility);
  };

  const handleToggleStatus = async (facility) => {
    const newStatus = facility.status === "active" ? "inactive" : "active";
    if (
      !window.confirm(
        `${newStatus === "active" ? "Enable" : "Disable"} this facility?`,
      )
    )
      return;
    setLoading(true);
    try {
      await facilitiesApi.update(facility.id, {
        ...facility,
        status: newStatus,
      });
      fetchFacilities();
    } catch (error) {
      console.error("Error toggling facility:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this facility? This cannot be undone.")) return;
    setLoading(true);
    const result = await facilitiesApi.delete(id);
    setLoading(false);
    if (result && result.error) {
      console.error("Error deleting facility:", result.error);
      alert(`Delete failed: ${result.error}`);
    } else {
      setFacilities((prev) => prev.filter((f) => f.id !== id));
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingFacility(null);
    setShowForm(false);
  };

  const getVerticalLabel = (id) =>
    VERTICALS.find((v) => v.value === id)?.label || id;
  const getTierLabel = (id) =>
    MODEL_TIERS.find((t) => t.value === id)?.label || id;
  const getModelLabel = (id) =>
    BASE_MODELS.find((m) => m.value === id)?.label || id;

  const filteredFacilities = facilities.filter((f) => {
    const fid = getField(f, "facility_id", "facilityId") || "";
    const fname = getField(f, "facility_name", "facilityName") || "";
    const email = getField(f, "contact_email", "contactEmail") || "";
    const matchesSearch =
      fname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || f.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (selectedFacility) {
    return (
      <FacilityDetail
        facility={selectedFacility}
        onBack={() => setSelectedFacility(null)}
        onSaved={fetchFacilities}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onRefresh={fetchFacilities}
            placeholder="Search facilities..."
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <PrimaryButton
          onClick={() =>
            onNavigate ? onNavigate("provision") : setShowForm(!showForm)
          }
          icon={Plus}
        >
          New Facility
        </PrimaryButton>
      </div>

      <FormModal
        title={editingFacility ? "Edit Facility" : "New Facility"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Facility ID *
              </label>
              <input
                type="text"
                required
                value={formData.facility_id}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    facility_id: e.target.value
                      .toUpperCase()
                      .replace(/\s/g, ""),
                  })
                }
                placeholder="e.g. HAF002, HOSP001"
                disabled={!!editingFacility}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Facility Name *
              </label>
              <input
                type="text"
                required
                value={formData.facility_name}
                onChange={(e) =>
                  setFormData({ ...formData, facility_name: e.target.value })
                }
                placeholder="Organization name"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Contact Phone
              </label>
              <input
                type="text"
                value={formData.contact_phone}
                onChange={(e) =>
                  setFormData({ ...formData, contact_phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Vertical
              </label>
              <select
                value={formData.vertical_id}
                onChange={(e) =>
                  setFormData({ ...formData, vertical_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {VERTICALS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Timezone
              </label>
              <select
                value={formData.timezone}
                onChange={(e) =>
                  setFormData({ ...formData, timezone: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {TIMEZONES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              SLM Training Configuration
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Base Model
                </label>
                <select
                  value={formData.base_model_id}
                  onChange={(e) =>
                    setFormData({ ...formData, base_model_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {BASE_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Hardware Tier
                </label>
                <select
                  value={formData.model_tier}
                  onChange={(e) =>
                    setFormData({ ...formData, model_tier: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {MODEL_TIERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.training_enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        training_enabled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">
                    Training Enabled
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              Webhook Notifications
            </h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Webhook URL
                </label>
                <input
                  type="url"
                  value={formData.webhook_url}
                  onChange={(e) =>
                    setFormData({ ...formData, webhook_url: e.target.value })
                  }
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.webhook_enabled}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        webhook_enabled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                  />
                  <span className="text-sm text-slate-700">Enabled</span>
                </label>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3">
              Chatbot / Branding Configuration
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData({ ...formData, display_name: e.target.value })
                  }
                  placeholder="Public-facing name"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Agent Name
                </label>
                <input
                  type="text"
                  value={formData.agent_name}
                  onChange={(e) =>
                    setFormData({ ...formData, agent_name: e.target.value })
                  }
                  placeholder="e.g. Lidy, Ben, Cal"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        state: e.target.value.toUpperCase().slice(0, 2),
                      })
                    }
                    placeholder="AZ"
                    maxLength={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Brand Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={formData.brand_color}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          brand_color: e.target.value,
                        })
                      }
                      className="w-10 h-10 rounded border border-slate-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.brand_color}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          brand_color: e.target.value,
                        })
                      }
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Static Base URL
                </label>
                <input
                  type="text"
                  value={formData.static_base_url}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      static_base_url: e.target.value,
                    })
                  }
                  placeholder="https://app.hyveappliedintelligence.com/static/lidy/AZLD4"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Allowed Origins (CORS)
                </label>
                <input
                  type="text"
                  value={formData.allowed_origins}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      allowed_origins: e.target.value,
                    })
                  }
                  placeholder="https://example.com,https://other.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Source Registry Namespace
                </label>
                <input
                  type="text"
                  value={formData.source_registry_namespace}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      source_registry_namespace: e.target.value,
                    })
                  }
                  placeholder="e.g. AZLD4_sources"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Sheets Log Tab
                </label>
                <input
                  type="text"
                  value={formData.sheets_log_tab}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sheets_log_tab: e.target.value,
                    })
                  }
                  placeholder="Logs"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                System Prompt
              </label>
              <textarea
                value={formData.system_prompt}
                onChange={(e) =>
                  setFormData({ ...formData, system_prompt: e.target.value })
                }
                rows={6}
                placeholder="AI agent system prompt..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Enrichment Rules (JSON)
              </label>
              <textarea
                value={formData.enrichment_rules}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    enrichment_rules: e.target.value,
                  })
                }
                rows={4}
                placeholder='[{"id":"...","label":"...","type":"info","pattern":"...","html":"..."}]'
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Facility"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Facility
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Vertical
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Services
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Users
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredFacilities.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No facilities found. Create one to get started.
                </td>
              </tr>
            ) : (
              filteredFacilities.map((facility) => {
                const fid =
                  getField(facility, "facility_id", "facilityId") || "-";
                const fname =
                  getField(facility, "facility_name", "facilityName") || "-";
                const email =
                  getField(facility, "contact_email", "contactEmail") || "";
                const phone =
                  getField(facility, "contact_phone", "contactPhone") || "";
                const vertical =
                  getField(facility, "vertical_id", "verticalId") || "";
                const model =
                  getField(facility, "base_model_id", "baseModelId") || "";
                const tier =
                  getField(facility, "model_tier", "modelTier") || "";
                const isActive = facility.status === "active";
                const isExpanded = expandedFacility === facility.id;
                const apiKey = getField(facility, "api_key", "apiKey") || "";
                const sftpUid = getField(facility, "sftp_uid", "sftpUid") || "";
                const onboardedAt = getField(
                  facility,
                  "onboarded_at",
                  "onboardedAt",
                );
                const trainingEnabled = getField(
                  facility,
                  "training_enabled",
                  "trainingEnabled",
                );
                const pineconeNs =
                  getField(
                    facility,
                    "pinecone_namespace",
                    "pineconeNamespace",
                  ) || "";
                const sourceRegistryNs =
                  getField(
                    facility,
                    "source_registry_namespace",
                    "sourceRegistryNamespace",
                  ) || "";
                const displayName =
                  getField(facility, "display_name", "displayName") || "";
                const agentName =
                  getField(facility, "agent_name", "agentName") || "";
                const brandColor =
                  getField(facility, "brand_color", "brandColor") || "";
                const facilityState = facility.state || "";

                return (
                  <React.Fragment key={facility.id}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() =>
                        setExpandedFacility(isExpanded ? null : facility.id)
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">
                          {fname}
                        </div>
                        <div className="text-xs text-slate-400 font-mono">
                          {fid}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        <div>{email || "-"}</div>
                        {phone && (
                          <div className="text-xs text-slate-400">{phone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700 capitalize">
                          {getVerticalLabel(vertical)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <FacilityServiceBadges services={facility.services} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 text-center">
                        {facility.user_count ?? "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleToggleStatus(facility)}
                            className={`p-1 rounded transition-colors ${isActive ? "text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"}`}
                            title={isActive ? "Disable" : "Enable"}
                          >
                            {isActive ? (
                              <PowerOff className="w-4 h-4" />
                            ) : (
                              <Power className="w-4 h-4" />
                            )}
                          </button>
                          <ActionButtons
                            onEdit={() => handleEdit(facility)}
                            onDelete={() => handleDelete(facility.id)}
                          />
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-4 bg-slate-50 border-t border-slate-100"
                        >
                          <div className="grid grid-cols-5 gap-6 text-sm">
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                Facility Details
                              </h4>
                              <div className="space-y-1 text-slate-600">
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Display Name:
                                  </span>{" "}
                                  {displayName || fname}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Address:
                                  </span>{" "}
                                  {facility.address || "-"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Timezone:
                                  </span>{" "}
                                  {facility.timezone || "-"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Onboarded:
                                  </span>{" "}
                                  {onboardedAt
                                    ? new Date(onboardedAt).toLocaleDateString()
                                    : "-"}
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                Chatbot Config
                              </h4>
                              <div className="space-y-1 text-slate-600">
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Agent:
                                  </span>{" "}
                                  {agentName || "-"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    State:
                                  </span>{" "}
                                  {facilityState || "-"}
                                </div>
                                <div className="flex items-center">
                                  <span className="text-xs text-slate-400 mr-1">
                                    Brand:
                                  </span>{" "}
                                  {brandColor ? (
                                    <>
                                      <span
                                        className="inline-block w-3 h-3 rounded-full mr-1 border border-slate-300"
                                        style={{ backgroundColor: brandColor }}
                                      />
                                      <span className="font-mono text-xs">
                                        {brandColor}
                                      </span>
                                    </>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Prompt:
                                  </span>{" "}
                                  {getField(
                                    facility,
                                    "system_prompt",
                                    "systemPrompt",
                                  )
                                    ? "Configured"
                                    : "Not set"}
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                SFTP / API
                              </h4>
                              <div className="space-y-1 text-slate-600">
                                <div>
                                  <span className="text-xs text-slate-400">
                                    SFTP UID:
                                  </span>{" "}
                                  {sftpUid || "Not onboarded"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    API Key:
                                  </span>{" "}
                                  {apiKey
                                    ? apiKey.substring(0, 8) + "..."
                                    : "Not set"}
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                Training
                              </h4>
                              <div className="space-y-1 text-slate-600">
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Training:
                                  </span>{" "}
                                  {trainingEnabled ? "Enabled" : "Disabled"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Base Model:
                                  </span>{" "}
                                  {getModelLabel(model)}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Hardware:
                                  </span>{" "}
                                  {getTierLabel(tier)}
                                </div>
                              </div>
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-700 mb-2">
                                RAG / Pinecone
                              </h4>
                              <div className="space-y-1 text-slate-600">
                                <div>
                                  <span className="text-xs text-slate-400">
                                    KB Namespace:
                                  </span>{" "}
                                  {pineconeNs || "Not provisioned"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Sources NS:
                                  </span>{" "}
                                  {sourceRegistryNs || "-"}
                                </div>
                                <div>
                                  <span className="text-xs text-slate-400">
                                    Webhook:
                                  </span>{" "}
                                  {getField(
                                    facility,
                                    "webhook_enabled",
                                    "webhookEnabled",
                                  )
                                    ? "Enabled"
                                    : "Disabled"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FacilityManagement;
